import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetMaterialsQuery } from '@/app/services/materials'
import { useGetRatesQuery } from '@/app/services/rates'
import { useGetRateTablesQuery } from '@/app/services/rateTables'
import { useGetSheetsQuery } from '@/app/services/sheets'
import { selectOrganisation, selectOrganisationId, selectUseTubeV2 } from '@/app/slices/organisationSlice'
import { getMaterialConsumptionModes } from '@/common/helpers/MaterialConsumptionModes'
import {
    doesProfileSizeFitSheet,
    getThicknessOptionValue,
    isThicknessWithinTolerance,
    PROFILE_TYPES,
} from '@/common/utils/SheetUtils/SheetUtils'

import useNumberFormatter from './useNumberFormatter'
import { useToolBoxTreatments } from './useToolBoxTreatments'

const compareMaterials = (a, b) => {
    if (a.materialName.toLocaleLowerCase() < b.materialName.toLocaleLowerCase()) {
        return -1
    }
    if (a.materialName.toLocaleLowerCase() > b.materialName.toLocaleLowerCase()) {
        return 1
    }
    return 0
}

// Open-section profiles have distinguishable legs, so W x H and H x W are different
// parts. Only Rectangle may match in either orientation.
const FIXED_ORIENTATION_PROFILES = new Set(['flatsidedoval', 'angle', 'uchannel', 'ibeam', 'tbeam'])
const hasFixedOrientation = (profile) => FIXED_ORIENTATION_PROFILES.has(profile?.toLowerCase())

export function useQuoteItemPropertyOptions({
    cuttingTechnologyId: cuttingTechnologyValue,
    materialId: materialValue,
    mode,
    partDimensions,
    profileDimensionsValue = null,
    profileValue = null,
    thickness: thicknessValue,
    webThickness: webThicknessValue = null,
}) {
    const { t } = useTranslation()

    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)
    const showTubeOptions = useSelector(selectUseTubeV2)
    const { showFullTubeLengths, showRenamedConsumptionModes } = useToolBoxTreatments()
    const { data: materials } = useGetMaterialsQuery({ organisationId })
    const { data: sheets } = useGetSheetsQuery({ organisationId })
    const { data: cuttingTechnologies } = useGetCuttingTechnologiesQuery({ organisationId })
    const { data: rateTables } = useGetRateTablesQuery({ organisationId })
    const { data: rates } = useGetRatesQuery({ organisationId })

    const { n } = useNumberFormatter()

    const customersMaterialOptions = [
        { label: t('Yes'), value: 'true' },
        { label: t('No'), value: 'false' },
    ]

    const isRotary = mode === 'rotary'
    const allConsumptionModes = getMaterialConsumptionModes(isRotary, showRenamedConsumptionModes)

    const consumptionModeOptions = useMemo(() => {
        return allConsumptionModes
            .filter((mode) => {
                // Filter out FullTubeLengths if flag is disabled or not rotary
                if (mode.value === 'FullTubeLengths') {
                    return isRotary && showFullTubeLengths
                }
                return true
            })
            .map((mode) => ({
                label: t(mode.name),
                value: mode.value,
            }))
    }, [allConsumptionModes, isRotary, showFullTubeLengths, t])

    const grainDirectionOptions = [
        { label: t('None'), value: 'None' },
        { label: t('Horizontal'), value: 'Horizontal' },
        { label: t('Vertical'), value: 'Vertical' },
    ]

    // Finds a matching profile dimension string, allowing reversed W x H matches only for
    // profiles without a fixed orientation (see hasFixedOrientation above).
    const findMatchingProfileDimension = useCallback(
        (targetDimension, availableOptions) => {
            if (!targetDimension || !availableOptions?.length) return null

            // Direct match first
            if (availableOptions.includes(targetDimension)) {
                return targetDimension
            }

            if (hasFixedOrientation(profileValue)) return null

            // For rectangular profiles, check if reversed dimensions match
            if (targetDimension.includes(' x ')) {
                const parts = targetDimension.split(' x ')
                if (parts.length === 2) {
                    const reversedDimension = `${parts[1]} x ${parts[0]}`
                    if (availableOptions.includes(reversedDimension)) {
                        return reversedDimension
                    }
                }
            }

            return null
        },
        [profileValue]
    )

    // Validate rate table thickness
    const sheetHasRateTableThickness = useCallback(
        (sheet, rateTable) => {
            if (!sheet || !rateTable || !rates) return false

            const sortedRates = Object.values(rates)
                .filter((rate) => rate.rateTableId === rateTable.rateTableId && !rate.isDeleted)
                .sort((a, b) => a.thickness - b.thickness)

            return (
                sortedRates.length > 0 &&
                sheet.thickness >= sortedRates[0].thickness &&
                sheet.thickness <= sortedRates[sortedRates.length - 1].thickness
            )
        },
        [rates]
    )

    const filterSheet = useCallback(
        (sheet) => {
            const cuttingTech = sheet?.sheetCuttingTechnology?.find(
                (cuttingTech) => cuttingTech?.cuttingTechnologyId === cuttingTechnologyValue
            )

            const isNotDisabled = cuttingTech?.isEnabled ?? true

            if (!organisation?.useTubeV2)
                return (
                    !sheet?.isDeleted && isNotDisabled && doesProfileSizeFitSheet(sheet, partDimensions, organisation)
                )

            return !sheet?.isDeleted && isNotDisabled
        },
        [cuttingTechnologyValue, organisation, partDimensions]
    )

    const getSheetsByMaterialId = useCallback(
        (materialId, applyFilter = true) => {
            if (!materials || !sheets || !materialId) return []

            const material = materials[materialId]
            if (!material?.sheetIds) return []

            let mappedSheets = material.sheetIds.map((sheetId) => sheets[sheetId]).filter(Boolean) // Remove undefined/null values

            // Apply filterSheet conditionally
            if (applyFilter) {
                mappedSheets = mappedSheets.filter(filterSheet)
            }

            return mappedSheets
        },
        [materials, sheets, filterSheet]
    )

    const getMaterialRateTable = useCallback(
        (material) => {
            if (!material?.materialRateTables?.length) return null

            return material.materialRateTables.find(
                (rateTable) => rateTables?.[rateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyValue
            )
        },
        [rateTables, cuttingTechnologyValue]
    )

    const matchesProfileDimensions = useCallback(
        (sheet) => {
            if (!profileValue || !profileDimensionsValue) return true
            if (sheet?.profile !== profileValue) return false

            // Match dimensions based on profile type
            if (profileValue === 'Circle') {
                return n(sheet.diameter) === profileDimensionsValue
            }
            if (profileValue === 'Square') {
                return n(sheet.sheetWidth) === profileDimensionsValue
            }
            if (hasFixedOrientation(profileValue)) {
                // Open-section profiles have a fixed orientation, unlike the rectangle default
                // below, so width/height must match exactly (no reversed-dimension fallback).
                const sheetDimensions = n(sheet?.sheetWidth) + ' x ' + n(sheet?.sheetHeight)
                return sheetDimensions === profileDimensionsValue
            }

            // Default case for other profile types
            const sheetDimensions = n(sheet?.sheetWidth) + ' x ' + n(sheet?.sheetHeight)
            const partDimensions = profileDimensionsValue.split(' x ')
            const partDimensionsReversed = partDimensions.reverse().join(' x ')

            return sheetDimensions === profileDimensionsValue || sheetDimensions === partDimensionsReversed
        },
        [profileValue, profileDimensionsValue, n]
    )

    // Helper function to format sheet size consistently
    const formatSheetSize = useCallback(
        (sheet) => {
            if (!sheet) return ''
            if (sheet.profile === 'Circle') return n(sheet.diameter)
            if (sheet.profile === 'Square') return n(sheet.sheetWidth)
            return n(sheet.sheetWidth) + ' x ' + n(sheet.sheetHeight)
        },
        [n]
    )

    const cuttingTechOptions = useMemo(() => {
        if (!cuttingTechnologies) return []

        return Object.values(cuttingTechnologies).filter(
            (tech) => tech?.type?.toLowerCase() === mode && !tech?.isDeleted
        )
    }, [cuttingTechnologies, mode])

    const materialRateTableFilter = useCallback(
        (materialRateTable) =>
            rateTables && rateTables[materialRateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyValue,
        [rateTables, cuttingTechnologyValue]
    )

    const materialFilter = useCallback(
        (material) =>
            material.materialRateTables?.some(materialRateTableFilter) &&
            !material.isDeleted &&
            material.type.toLowerCase() === mode,
        [materialRateTableFilter, mode]
    )

    const materialOptions = useMemo(() => {
        if (!materials) return []

        return Object.values(materials).filter(materialFilter)?.sort(compareMaterials)
    }, [materials, materialFilter])

    const profileOptions = useMemo(() => {
        if (mode === 'flat' || !showTubeOptions) return []
        if (!materialValue) return []

        const sheetsForMaterial = getSheetsByMaterialId(materialValue)

        const uniqueProfiles = new Set()
        sheetsForMaterial?.forEach((sheet) => {
            if (sheet?.profile) uniqueProfiles.add(sheet.profile)
        })
        return Array.from(uniqueProfiles).sort()
    }, [materialValue, mode, showTubeOptions, getSheetsByMaterialId])

    const profileDimensions = useMemo(() => {
        if (mode === 'flat' || !showTubeOptions) return []
        if (!materialValue || !profileValue) return []

        const sheetsForMaterial = getSheetsByMaterialId(materialValue)?.filter(
            (sheet) => sheet?.profile === profileValue
        )

        const profileDimensions = sheetsForMaterial?.map(formatSheetSize).sort((a, b) => {
            if (a < b) return -1
            if (a > b) return 1
            return 0
        })
        return [...new Set(profileDimensions ?? [])]
    }, [materialValue, mode, showTubeOptions, profileValue, getSheetsByMaterialId, formatSheetSize])

    const thicknessOptions = useMemo(() => {
        if (!materials || !sheets || !materialValue) return []

        const material = materials[materialValue]
        if (!material) return []

        const materialRateTable = getMaterialRateTable(material)

        const rateTableToCheck = rateTables?.[materialRateTable?.rateTableId]

        // QM-3717: keyed on (webThickness, thickness) for IBeam so two records sharing a flange
        // thickness but differing in web thickness stay distinct options; every other profile
        // keys on thickness alone, unchanged from the pre-QM-3717 behaviour.
        const thicknessOptionsByValue = new Map()

        getSheetsByMaterialId(materialValue)
            ?.filter((sheet) => sheetHasRateTableThickness(sheet, rateTableToCheck))
            .filter((sheet) => {
                if (mode === 'flat' || !profileValue) return true
                // A material can mix profiles (e.g. Angle sheets alongside IBeam ones), so profile
                // identity is checked here regardless of whether dimensions are known yet.
                if (sheet?.profile !== profileValue) return false
                return profileDimensionsValue ? matchesProfileDimensions(sheet) : true
            })
            .forEach((sheet) => {
                if (sheet?.thickness === undefined) return

                const { profile, thickness, webThickness } = sheet
                const value = getThicknessOptionValue({ profile, thickness, webThickness })
                if (!thicknessOptionsByValue.has(value)) {
                    thicknessOptionsByValue.set(value, { profile, thickness, value, webThickness })
                }
            })

        return Array.from(thicknessOptionsByValue.values()).sort(
            (a, b) => a.thickness - b.thickness || (a.webThickness ?? 0) - (b.webThickness ?? 0)
        )
    }, [
        materialValue,
        materials,
        sheets,
        getSheetsByMaterialId,
        sheetHasRateTableThickness,
        rateTables,
        mode,
        profileValue,
        profileDimensionsValue,
        matchesProfileDimensions,
        getMaterialRateTable,
    ])

    const sheetOptions = useMemo(() => {
        if (!materials || !sheets || !materialValue || !thicknessValue) return []

        const material = materials[materialValue]
        if (!material) return []

        let sheetsForMaterial = getSheetsByMaterialId(materialValue)?.filter(
            (sheet) => sheet?.thickness === thicknessValue
        )

        // QM-3717: narrow to the selected web thickness — IBeam only. Every other profile never
        // carries a webThickness, so scope the check explicitly rather than relying on both sides
        // defaulting to null (a material can mix profiles, e.g. Angle sheets alongside IBeam ones).
        sheetsForMaterial = sheetsForMaterial.filter((sheet) => {
            if (sheet?.profile !== PROFILE_TYPES.I_BEAM) return true
            return (sheet?.webThickness ?? null) === (webThicknessValue || null)
        })

        if (mode !== 'flat') {
            sheetsForMaterial = sheetsForMaterial.filter((sheet) =>
                isThicknessWithinTolerance(sheet, thicknessValue, partDimensions, organisation)
            )

            if (profileValue) {
                // A material can mix profiles (e.g. Angle sheets alongside IBeam ones), so profile
                // identity is checked here regardless of whether dimensions are known yet.
                sheetsForMaterial = sheetsForMaterial.filter((sheet) => sheet?.profile === profileValue)

                // Apply profile dimension filtering — skipped for IBeam: (thickness, webThickness)
                // already uniquely identifies a record within a material (see
                // getThicknessOptionValue), and profileDimensionsValue is unreliable here —
                // clearing item.sheet on a thickness change (ItemSettings.jsx
                // handleThicknessChange, QM-3717) drops it to the item's raw CAD dimensions, which
                // aren't the same measurement as the sheet's own sheetWidth x sheetHeight.
                // Requiring a match would strand the item with an empty sheetOptions and no way to
                // recover (loadDefaultSheetOption never runs, the item stays pending forever).
                if (profileValue !== PROFILE_TYPES.I_BEAM && profileDimensionsValue) {
                    sheetsForMaterial = sheetsForMaterial.filter(matchesProfileDimensions)
                }
            }
        }

        if (!sheetsForMaterial?.length) return []

        return sheetsForMaterial.map((sheet) => {
            const sheetSize = formatSheetSize(sheet)
            if (sheet.sheetSize === sheetSize) return sheet

            return { ...sheet, sheetSize }
        })
    }, [
        materialValue,
        thicknessValue,
        webThicknessValue,
        materials,
        sheets,
        getSheetsByMaterialId,
        formatSheetSize,
        mode,
        partDimensions,
        organisation,
        profileValue,
        profileDimensionsValue,
        matchesProfileDimensions,
    ])

    return {
        cuttingTechOptions,
        materialOptions,
        thicknessOptions,
        sheetOptions,
        customersMaterialOptions,
        consumptionModeOptions,
        grainDirectionOptions,
        profileOptions,
        profileDimensions,
        findMatchingProfileDimension,
    }
}
